import { z } from 'zod';

/**
 * Branded identifiers.
 *
 * All Firestore document ids are strings, which makes it trivially easy to pass
 * a match id where a team id is expected. Branding makes those mistakes a
 * compile error at no runtime cost: the values stay plain strings.
 */
const documentId = z.string().trim().min(1).max(128);

export const tournamentIdSchema = documentId.brand<'TournamentId'>();
export const teamIdSchema = documentId.brand<'TeamId'>();
export const matchIdSchema = documentId.brand<'MatchId'>();
export const boardIdSchema = documentId.brand<'BoardId'>();
export const uidSchema = documentId.brand<'Uid'>();

/**
 * Identifies a phase within a tournament, e.g. `gruppenphase` or `ko`.
 * Chosen by the tournament director in the settings, not by the code.
 */
export const phaseIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche')
  .brand<'PhaseId'>();

/**
 * Identifies a round inside a phase, e.g. `gruppenphase.spieltag-1` or
 * `ko.finale`. Used as the most specific key when resolving the match rules,
 * which is what allows "Finale = Best of 9" to be pure configuration.
 */
export const roundKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(96)
  .regex(/^[a-z0-9][a-z0-9.-]*$/, 'Nur Kleinbuchstaben, Ziffern, Punkte und Bindestriche')
  .brand<'RoundKey'>();

/** Identifies a group inside a group phase, e.g. `a`, `b`. */
export const groupIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche')
  .brand<'GroupId'>();

export type TournamentId = z.infer<typeof tournamentIdSchema>;
export type TeamId = z.infer<typeof teamIdSchema>;
export type MatchId = z.infer<typeof matchIdSchema>;
export type BoardId = z.infer<typeof boardIdSchema>;
export type Uid = z.infer<typeof uidSchema>;
export type PhaseId = z.infer<typeof phaseIdSchema>;
export type RoundKey = z.infer<typeof roundKeySchema>;
export type GroupId = z.infer<typeof groupIdSchema>;
